import type { Store } from '@blocksuite/affine/store';

/**
 * URL scheme for the source-side marker that points at a branched
 * sub-doc. Distinct from the canvas-card scheme so the global
 * CanvasLinkRouter can route each to its own navigation handler.
 */
export const BRANCH_LINK_SCHEME = 'curiescious-branch://';

export function branchHref(branchDocId: string): string {
  return `${BRANCH_LINK_SCHEME}${encodeURIComponent(branchDocId)}`;
}

export function parseBranchHref(href: string): string | null {
  if (!href.startsWith(BRANCH_LINK_SCHEME)) return null;
  try {
    return decodeURIComponent(href.slice(BRANCH_LINK_SCHEME.length));
  } catch {
    return null;
  }
}

/**
 * Teal accent for branch markers — deliberately different from the
 * type-coloured canvas-card highlights so a passage can carry BOTH
 * decorations (a card AND a branch) without colour collision. The
 * background is intentionally subtle; the underline is the dominant
 * visual signal.
 */
const BRANCH_BACKGROUND = 'rgba(15, 118, 110, 0.18)';

type FormatTarget = {
  text?: {
    format: (
      index: number,
      length: number,
      attrs: Record<string, unknown>
    ) => void;
    toString: () => string;
  } | null;
};

/**
 * Paint the source-side marker for a branch: a teal background tint
 * + a `link` carrying the branch doc id. We keep using the existing
 * BlockSuite `background` inline format (the same primitive cards
 * use) rather than a custom underline-only style so the visual stack
 * with canvas-card highlights is predictable: when BOTH a card and a
 * branch exist on the same range, the user sees both tints layered.
 */
export function applyBranchLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number },
  branchDocId: string
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  if (range.length <= 0) return;
  try {
    target.text.format(range.index, range.length, {
      background: BRANCH_BACKGROUND,
      link: branchHref(branchDocId),
    });
  } catch (err) {
    console.warn('[branch-link] format failed', err);
  }
}

/**
 * Clear the branch marker on undo. Sets background + link to null so
 * the inline format is removed (BlockSuite treats null as "clear this
 * attribute on the range").
 */
export function removeBranchLink(
  doc: Store,
  sourceBlockId: string,
  range: { index: number; length: number }
): void {
  const target = doc.getBlock(sourceBlockId)?.model as FormatTarget | undefined;
  if (!target?.text) return;
  if (range.length <= 0) return;
  try {
    target.text.format(range.index, range.length, {
      background: null,
      link: null,
    });
  } catch {
    /* range gone */
  }
}
